import React from 'react';
import Breadcrumb from '../Breadcrumb';
import Card from '../Card';
import { CardSkeleton } from '../LoadingSkeleton';

export interface BreadcrumbCrumb {
  label: string;
  path?: string;
}

export type MetricAccent = 'blue' | 'green' | 'amber' | 'purple' | 'red';

export interface DetailMetric {
  label: string;
  value: React.ReactNode; // already-formatted (e.g. "R12,000", number)
  accent?: MetricAccent;
}

export interface EntityDetailLayoutProps {
  breadcrumb: BreadcrumbCrumb[]; // full parent chain incl. current (last = bold)
  title: string;
  subtitle?: string;
  headerBadges?: React.ReactNode; // <Badge status/> / <Badge priority/>
  metrics?: DetailMetric[];
  progress?: { percentage: number; label?: string };
  actions?: React.ReactNode; // Edit / Delete buttons
  loading?: boolean;
  error?: string | null;
  children?: React.ReactNode; // <DetailChildList/> section(s)
}

const accentClasses: Record<MetricAccent, { tile: string; value: string }> = {
  blue: { tile: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700', value: 'text-blue-700 dark:text-blue-400' },
  green: { tile: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700', value: 'text-green-700 dark:text-green-400' },
  amber: { tile: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700', value: 'text-amber-700 dark:text-amber-400' },
  purple: { tile: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700', value: 'text-purple-700 dark:text-purple-400' },
  red: { tile: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700', value: 'text-red-700 dark:text-red-400' },
};

const EntityDetailLayout: React.FC<EntityDetailLayoutProps> = ({
  breadcrumb,
  title,
  subtitle,
  headerBadges,
  metrics,
  progress,
  actions,
  loading = false,
  error = null,
  children,
}) => {
  return (
    <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <Breadcrumb items={breadcrumb} />

      {loading ? (
        <div className="space-y-6">
          <CardSkeleton showFooter={false} />
          <CardSkeleton />
        </div>
      ) : error ? (
        <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          {error}
        </div>
      ) : (
        <>
          {/* Header */}
          <Card variant="glass" className="mb-6">
            <Card.Body>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">
                    {title}
                  </h1>
                  {subtitle && (
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>
                  )}
                  {headerBadges && (
                    <div className="flex flex-wrap gap-2 items-center mt-3">{headerBadges}</div>
                  )}
                </div>
                {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
              </div>

              {metrics && metrics.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  {metrics.map((metric, index) => {
                    const accent = accentClasses[metric.accent ?? 'blue'];
                    return (
                      <div key={index} className={`p-4 rounded-lg border ${accent.tile}`}>
                        <div className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">
                          {metric.label}
                        </div>
                        <div className={`text-3xl font-bold ${accent.value}`}>{metric.value}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {progress && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {progress.label ?? 'Overall Completion'}
                    </span>
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                      {progress.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300"
                      style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Child list section(s) */}
          {children && <div className="space-y-6">{children}</div>}
        </>
      )}
    </div>
  );
};

export default EntityDetailLayout;
